import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import type { ReduxState } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import { StakeType } from 'app/lib/staking/constants';
import { createGaEvent, dispatchGaEvent } from 'app/packages/analytics';

import { SubmissionState } from '../../constants';
import type { RecordOfBetInfoType } from '../../records';
import { selectTotalAmountActive } from '../../selectors/entries';
import { selectProcessingEntry } from '../../selectors/oneClickBet';
import { selectCurrentStakeType } from '../../selectors/stakeTypes';
import { selectExpressSubmission } from '../../selectors/stakeTypes/express';
import { selectSingleBetsInfo } from '../../selectors/stakeTypes/single';
import { selectSystemSubmission } from '../../selectors/stakeTypes/system';
import SubmissionIcon from './components/Icon';

import dictionary from './Submission.dictionary.json';

import styles from './Submission.module.scss';

// ---

type StoreProps = {
  entrySubmission: SubmissionState;
  isAwaitingBet: boolean;
  isSingle: boolean;
  totalAmountActive: boolean;
};

type SubmissionProps = {
  betId?: number;
  isOneClickBet?: boolean;
  className?: string;
};

const SUBMISSION_STATE_STATUSES = {
  [SubmissionState.PROCESSING]: 'processing',
  [SubmissionState.ACCEPTED]: 'accepted',
  [SubmissionState.REJECTED]: 'rejected',
  [SubmissionState.FAILED]: 'failed',
};

const SUBMISSION_STATE_TITLES = {
  [SubmissionState.PROCESSING]: dictionary.processing,
  [SubmissionState.ACCEPTED]: dictionary.accepted,
  [SubmissionState.REJECTED]: dictionary.rejected,
  [SubmissionState.FAILED]: dictionary.rejected,
};

const Submission: FC<SubmissionProps & StoreProps> = ({
  entrySubmission,
  isAwaitingBet,
  className,
  isSingle,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (entrySubmission === SubmissionState.ACCEPTED) {
      dispatchGaEvent(createGaEvent({ event: 'cart-bidConfirm' }));
    }
  }, [entrySubmission]);

  if (isAwaitingBet || entrySubmission === SubmissionState.AWAITING) {
    return null;
  }
  const submissionStatus = SUBMISSION_STATE_STATUSES[entrySubmission];
  return (
    <div className={classNames(styles.submissionContainer, className, styles[submissionStatus],
      {
        [styles.single]: isSingle,
      },
    )}
         data-test="submission-container">
      <div className={classNames(styles.contentWrapper)}>
        <SubmissionIcon submission={entrySubmission} />
        <p className={classNames(styles.submissionTitle, styles[submissionStatus])}>
          {t(SUBMISSION_STATE_TITLES[entrySubmission])}
        </p>
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState, {
  betId,
  isOneClickBet,
}: SubmissionProps): StoreProps => {
  let entrySubmission = SubmissionState.AWAITING;
  const stakeType = selectCurrentStakeType(state);
  if (isOneClickBet) {
    const entry = selectProcessingEntry(state);

    if (entry) {
      entrySubmission = entry.betInfo.submission;
    }
    else {
      console.error('Submission -> mapStateToProps -> oneClickBet entry not found');
    }
  }
  else if (betId) {
    const betsInfo = selectSingleBetsInfo(state);
    const betInfo: RecordOfBetInfoType | undefined = betsInfo.get(betId);

    if (betInfo) {
      entrySubmission = betInfo.get('submission');
    }
    else {
      console.error('Submission -> mapStateToProps -> `betInfo` not found');
    }
  }
  else {
    if (stakeType.has(StakeType.EXPRESS)) {
      entrySubmission = selectExpressSubmission(state);
    }
    else if (stakeType.has(StakeType.SYSTEM)) {
      entrySubmission = selectSystemSubmission(state);
    }
  }

  return {
    entrySubmission,
    isSingle: stakeType.has(StakeType.SINGLE),
    totalAmountActive: selectTotalAmountActive(state),
    isAwaitingBet: entrySubmission === SubmissionState.AWAITING,
  };
};

export default connect(mapStateToProps)(Submission);
